import ApplicationController from '../application_controller';

export default class extends ApplicationController {
    static targets = [
        'emailText', 'approvedInputsSection', 'startAt', 'endAt', 'customerType',
        'activeSubscriptionEndAt', 'customerContactedAt', 'durationInYears', 'subscriptionTypeId',
        'addressName', 'addressStreet', 'addressZip', 'addressCity', 'addressCountry', 'quantity',
        'lastActiveEndAt', 'lastActiveStartAt', 'subscriptionInfoBox', 'subscriptionInfoIcon',
        'requiredSubscriptionsCount', 'subscriptionsNeededToAddCount', 'dueDate'
    ]

    initialize() {
        this.assignFlatpickr(this.customerContactedAtTarget, 'setCustomerContactedAt');
        this.assignFlatpickr(this.dueDateTarget, 'setDueDateAt');
        this.assignFlatpickr(this.startAtTarget, 'setStartAt');
        this.assignFlatpickr(this.endAtTarget, 'setEndAt');
        this.setQuantityBasedOnPeriod();
        let selectedRadio = document.querySelector('.actions [type="radio"]:checked');
        if (selectedRadio) {
            this.showByStatus(selectedRadio.value)
        }
    }

    assignFlatpickr(input, methodName) {
        flatpickr(input, {
            locale: this.getLocale(),
            dateFormat: 'Y-m-d',
            altInput: true,
            altFormat: "d. m. Y"
        });

        this[methodName]?.();
    }

    showByStatus(status) {
        let contentToEmailText;
        const emailText = this.emailTextTarget;
        if (status.includes('_')) {
            contentToEmailText = this.toCamelCase(status) + 'EmailText'
        } else {
            contentToEmailText = status + 'EmailText'
        }
        emailText.innerHTML = emailText.dataset[contentToEmailText];
        emailText.parentElement.classList.remove('hidden');
        const displayApprovedInputs = status === 'approve';
        this.setStartAt();
        this.setEndAt();
        this.setDueDateAt();
        this.approvedInputsSectionTarget.style.display = displayApprovedInputs ? 'block' : 'none';
    }

    statusChanged(event) {
        const status = event.target.value;
        this.showByStatus(status)
    }

    customerChanged(event) {
        const selectedOption = event.target.selectedOptions[0];
        const customerId = event.target.value;
        const customerType = selectedOption?.dataset?.customerType || '';

        this.customerTypeTarget.value = customerType;

        const form = this.element.querySelector('form');
        if (!form) return;

        const formData = new FormData(form);
        const searchParams = new URLSearchParams();

        for (const [key, value] of formData.entries()) {
            if (key !== 'authenticity_token' && key !== 'utf8') {
                searchParams.append(key, value);
            }
        }

        searchParams.set('billing_subscription_proposal_form[customer_id]', customerId);
        searchParams.set('billing_subscription_proposal_form[customer_type]', customerType);

        this.showOverlay();

        window.location.search = searchParams.toString();
    }

    contactedAtChanged() {
        this.setStartAt();
        this.setEndAt();
        this.setDueDateAt();
    }

    checkQuantity() {
        const quantity = parseInt(this.quantityTarget.value) || 0;
        const required = parseInt(this.requiredSubscriptionsCountTarget.value) || 0;

        if (quantity < required) {
            this.subscriptionInfoBoxTarget.classList.add('warning');
            this.subscriptionInfoIconTarget.classList.add('no');
        }
    }

    setCustomerContactedAt() {
        if (!this.customerContactedAtTarget.value) {
            this.customerContactedAtTarget._flatpickr.setDate(new Date());
        }
    }

    setStartAt() {
        const activeEndAt = this.lastActiveEndAtTarget.value || null;
        const activeStartAt = this.lastActiveStartAtTarget.value || null;
        const contactedAt = this.customerContactedAtTarget.value || null;

        const requiredSubscriptions = parseInt(this.requiredSubscriptionsCountTarget.value) || 0;
        const neededSubscriptions = parseInt(this.subscriptionsNeededToAddCountTarget.value) || 0;

        const activeEndDate = activeEndAt ? new Date(activeEndAt) : null;
        const activeStartDate = activeStartAt ? new Date(activeStartAt) : null;
        const contactedDate = contactedAt ? new Date(contactedAt) : null;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

        let startAtDate;

        // 1. No active subscription
        if (!activeEndDate) {
            startAtDate = contactedDate || today;

        // 2. Last subscription ended more than 3 months ago
        } else if (activeEndDate < threeMonthsAgo) {
            startAtDate = contactedDate || today;

        // 3. Full coverage (needed = 0) → start new period
        } else if (neededSubscriptions === 0) {
            startAtDate = new Date(activeEndDate);
            startAtDate.setDate(startAtDate.getDate() + 1);

        // 4. Ended less than 3 months ago
        } else if (activeEndDate < today) {
            startAtDate = new Date(activeEndDate);
            startAtDate.setDate(startAtDate.getDate() + 1);

        // 5. Active in the future but additional subscriptions are needed
        } else if (activeEndDate >= today && requiredSubscriptions > 0) {
            startAtDate = activeStartDate;
        // 6. Default: active in the future and no additional subscriptions needed
        } else {
            startAtDate = new Date(activeEndDate);
            startAtDate.setDate(startAtDate.getDate() + 1);
        }

        this.startAtTarget._flatpickr.setDate(startAtDate);
    }

    setEndAt() {
        const startAt = this.startAtTarget.value;
        const durationInYears = this.calculateDurationInYears();

        if (!startAt || !durationInYears) return;

        const startAtDate = new Date(startAt);
        startAtDate.setDate(startAtDate.getDate() - 1);
        startAtDate.setFullYear(startAtDate.getFullYear() + durationInYears);

        this.endAtTarget._flatpickr.setDate(startAtDate);
    }

    setDueDateAt() {
        const startAt = new Date(this.startAtTarget.value);
        const dueDays = parseInt(this.dueDateTarget.dataset.dueDays);
        startAt.setDate(startAt.getDate() + dueDays);

        this.dueDateTarget._flatpickr.setDate(startAt);
    }

    setQuantityBasedOnPeriod() {
        const required = parseInt(this.requiredSubscriptionsCountTarget.value) || 0;
        const needed = parseInt(this.subscriptionsNeededToAddCountTarget.value);

        if (needed === 0) {
            this.quantityTarget.value = required;
            this.setStartAt();
            this.setEndAt();
            this.setDueDateAt();
        } else {
            this.quantityTarget.value = needed;
            this.startAtTarget._flatpickr.setDate(new Date(this.lastActiveStartAtTarget.value));
            this.endAtTarget._flatpickr.setDate(new Date(this.lastActiveEndAtTarget.value));
        }
    }

    calculateDurationInYears() {
        const selectedOption = this.subscriptionTypeIdTarget.selectedOptions[0];
        const periodLength = selectedOption?.dataset?.periodLength;

        if (periodLength) {
            this.durationInYearsTarget.value = periodLength;
            return parseInt(periodLength);
        }

        return null;
    }

    fillAddressFields(event) {
        const selected = event.target.selectedOptions[0].dataset;

        this.addressNameTarget.value = selected.addressName || '';
        this.addressStreetTarget.value = selected.addressStreet || '';
        this.addressZipTarget.value = selected.addressZip || '';
        this.addressCityTarget.value = selected.addressCity || '';
        this.addressCountryTarget.value = selected.addressCountry || '';
    }
}
